import type { Policy } from 'ui/components/renters/summary/small/index.vue';

import { KaminoPolicyStatus } from '~/graphql/mma/generated/graphql';
import { useEntity as useEntityQuery } from '~/composables/urql/useEntity';
import type { EntityFragmentFragment, KaminoPolicyFragmentFragment, SkypadPolicyFragmentFragment } from '~/graphql/mma/generated/graphql';

export const useEntityUuid = () => useState<string>('uuid', () => '');

export const useEntityOrUndefined = () => {
  const uuid = useEntityUuid();
  const {
    fetching, error, data, executeQuery,
  } = useEntityQuery(
    computed(() => ({ uuid: uuid.value })),
    { pause: computed(() => !uuid.value) },
  );

  // looks like the fetching is set to false, and after the data filled.
  // So there is a moment when the fetching is false, but the data is not filled.
  // Therefore we need to check if the data is filled to set the loading to false
  const loading = computed(() => fetching.value || (!!uuid.value && !error.value && !data.value));

  return {
    refetch: () => executeQuery({ requestPolicy: 'network-only' }),
    fetching,
    error,
    data,
    loading,
    uuid,
  };
};

function skypadQualifiesForQuickActions(policy: SkypadPolicyFragmentFragment): boolean {
  return !!policy.isModifiable && !policy.isImported;
}

/** Kamino policy qualifies when isModifiable (active/pre-active, source != policy import). */
function kaminoQualifiesForQuickActions(policy: KaminoPolicyFragmentFragment): boolean {
  return policy.isModifiable;
}

export const useEntity = () => {
  const {
    data,
    refetch,
    fetching,
    error,
  } = useEntityOrUndefined();
  const entity = computed<EntityFragmentFragment>(() => {
    if (!data.value?.entity) {
      throw new Error('Entity is not set');
    }
    return data.value.entity;
  });
  /** Show quick actions box when entity has at least one Skypad or Kamino policy that is active/pre-active, not policy import, and can_modify. */
  const thereIsModifiableRentersPolicy = computed(() => (
    entity.value.skypadPolicies?.some(skypadQualifiesForQuickActions) ?? false
  ) || (entity.value.kaminoPolicies?.some(kaminoQualifiesForQuickActions) ?? false));
  const modifiablePoliciesSkypad = computed<SkypadPolicyFragmentFragment[]>(() => entity.value.skypadPolicies?.filter(
    (policy) => skypadQualifiesForQuickActions(policy) && !!policy.insured.school,
  ) ?? []);
  const modifiablePoliciesKamino = computed<KaminoPolicyFragmentFragment[]>(() => entity.value.kaminoPolicies?.filter(kaminoQualifiesForQuickActions) ?? []);
  const skypadWithDocs = computed(() => entity.value.skypadPolicies?.filter((policy) => policy.documents && policy.documents.length > 0) ?? []);
  const kaminoWithDocs = computed<KaminoPolicyFragmentFragment[]>(() => entity.value.kaminoPolicies?.filter((policy) => policy.documents && policy.documents.length > 0) ?? []);
  /** Kamino documents are only generated after conversion to active; hide view-documents for pre-active. */
  const kaminoActiveWithDocs = computed<KaminoPolicyFragmentFragment[]>(() => kaminoWithDocs.value.filter((p) => p.policy_status === KaminoPolicyStatus.Active));
  const thereIsViewablePolicyDocuments = computed(() => skypadWithDocs.value.length > 0 || kaminoActiveWithDocs.value.length > 0);
  const faberWithDocs = computed(() => entity.value.faberPolicies?.filter((policy) => policy.documents && policy.documents.length > 0) ?? []);

  const rentersPolicies = computed<Policy[]>(() => {
    const policies: Policy[] = [];
    entity.value?.skypadPolicies?.forEach((policy) => {
      policies.push({
        policyNumber: policy.policy_number,
        startDate: policy.coverage_start_date,
        endDate: policy.coverage_end_date,
        autoRenew: policy.isAutoRenew,
        status: policy.policy_status,
      });
    });
    entity.value?.kaminoPolicies?.forEach((policy) => {
      policies.push({
        policyNumber: policy.policyNumber,
        startDate: policy.policy_start_date,
        endDate: undefined,
        autoRenew: policy.isAutoRenew,
        status: policy.policy_status,
      });
    });
    return policies.sort((a, b) => (b.startDate < a.startDate ? -1 : 1));
  });

  return {
    refetch,
    fetching,
    error,
    entity,
    thereIsModifiableRentersPolicy,
    thereIsViewablePolicyDocuments,
    modifiablePoliciesSkypad,
    modifiablePoliciesKamino,
    skypadWithDocs,
    kaminoActiveWithDocs,
    faberWithDocs,
    rentersPolicies,
  };
};
